import React, { useState, Fragment, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { v4 as uuidv4 } from "uuid";
import toast from "react-hot-toast";
import { formatUnitDisplay } from "../../../../utils/reportVariableUnits";
import {
  RV_DIALOG_Z,
  RV_OVERLAY_CLASS,
  RV_CENTER_WRAPPER,
  RV_CENTER_INNER,
  rvTransitionOverlay,
  rvTransitionPanel,
  RV_PANEL_XL,
  RV_DIALOG_TITLE,
  RV_INPUT_COMPACT,
  RV_BTN_OUTLINE,
  RV_BTN_PRIMARY_DARK,
} from "./report-variable-dialog-theme";

const BulkAddVariablesDialog = ({ 
  open, 
  onClose, 
  onSave, 
  typeGroup,
  collaborationAnchor,
  report,
  canEdit 
}) => {
  const [variables, setVariables] = useState([
    {
      id: uuidv4(),
      reference: "",
      projected_quantity: "",
      pre_measurement_quantity: "",
    },
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const inputRefs = useRef({});

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setVariables([
        {
          id: uuidv4(),
          reference: "",
          projected_quantity: "",
          pre_measurement_quantity: "",
        },
      ]);
      setIsSaving(false);
    }
  }, [open]);

  const handleVariableChange = (variableId, field, value) => {
    setVariables(
      variables.map((variable) =>
        variable.id === variableId ? { ...variable, [field]: value } : variable
      )
    );
  };

  const handleReferenceBlur = (variable) => {
    // If reference is filled and this is the last row, add a new row
    // But don't auto-focus - let user tab naturally through fields
    if (variable.reference && variable.reference.trim() !== "") {
      const isLastRow = variables[variables.length - 1].id === variable.id;
      if (isLastRow) {
        const newVariable = {
          id: uuidv4(),
          reference: "",
          projected_quantity: "",
          pre_measurement_quantity: "",
        };
        setVariables([...variables, newVariable]);
        // Don't auto-focus - let user continue tabbing through current row
      }
    }
  };

  const handleReferenceKeyDown = (e, variable, index) => {
    if (e.key === "Tab" && !e.shiftKey) {
      // Tab from reference should go to projected quantity in same row
      e.preventDefault();
      const projectedInputRef = inputRefs.current[`projected-${variable.id}`];
      if (projectedInputRef) {
        projectedInputRef.focus();
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Enter from reference should go to projected quantity in same row
      const projectedInputRef = inputRefs.current[`projected-${variable.id}`];
      if (projectedInputRef) {
        projectedInputRef.focus();
      }
    }
  };

  const handleRemoveRow = (variableId) => {
    if (variables.length > 1) {
      setVariables(variables.filter((variable) => variable.id !== variableId));
    }
  };

  const handleSave = async () => {
    // Filter out empty rows - only keep rows with reference
    const validVariables = variables.filter(
      (variable) => variable.reference && variable.reference.trim() !== ""
    );

    if (validVariables.length === 0) {
      toast.error("Ingen gyldige variabler å legge til. Fyll ut referanse.");
      return;
    }

    if (!typeGroup) {
      toast.error("Type gruppe må være valgt");
      return;
    }

    setIsSaving(true);
    try {
      // Create all variables
      const variablesToAdd = validVariables.map((variable) => ({
        reference: variable.reference.trim(),
        projected_quantity: variable.projected_quantity ? parseFloat(variable.projected_quantity) : null,
        pre_measurement_quantity: variable.pre_measurement_quantity ? parseFloat(variable.pre_measurement_quantity) : null,
        projected_unit_id: typeGroup.projected_unit_id || null,
        measurement_unit_id: typeGroup.measurement_unit_id,
        variable_type_id: typeGroup.report_variable_type_id,
        type_group_id: typeGroup.id,
        group_id: null, // Will be set by parent if needed
        is_main_variable: false,
      }));

      // Call onSave with all variables
      if (onSave) {
        await onSave(variablesToAdd);
      }

      toast.success(`${validVariables.length} ${validVariables.length === 1 ? "variabel" : "variabler"} lagt til`);
      onClose();
    } catch (error) {
      if (collaborationAnchor) return;
      console.error("Error adding variables:", error);
      toast.error("Kunne ikke legge til variabler");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className={RV_DIALOG_Z} onClose={onClose}>
        <Transition.Child as={Fragment} {...rvTransitionOverlay}>
          <div className={RV_OVERLAY_CLASS} />
        </Transition.Child>

        <div className={RV_CENTER_WRAPPER}>
          <div className={RV_CENTER_INNER}>
            <Transition.Child as={Fragment} {...rvTransitionPanel}>
              <Dialog.Panel className={`${RV_PANEL_XL} !transform-none`}>
                <div ref={collaborationAnchor} />
                <Dialog.Title className={`mb-4 ${RV_DIALOG_TITLE}`}>
                  Legg til variabler i bulk
                </Dialog.Title>

                {typeGroup && (
                  <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50/80 p-4">
                    <div className="mb-1 text-xs font-medium text-gray-500">
                      Type gruppe
                    </div>
                    <div className="text-sm text-gray-900">
                      {typeGroup.report_variable_type?.displayTitle || "-"}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Måleenhet: {formatUnitDisplay(typeGroup.measurement_unit)}
                    </div>
                    {typeGroup.projected_unit && (
                      <div className="text-xs text-gray-500 mt-1">
                        Prosjektert enhet: {formatUnitDisplay(typeGroup.projected_unit)}
                      </div>
                    )}
                  </div>
                )}

                {/* Table */}
                <div className="mb-4 max-h-[min(55vh,28rem)] overflow-x-auto overflow-y-auto sm:max-h-[500px]">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="border-r border-gray-200 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-4">
                          Referanse *
                        </th>
                        <th className="min-w-[120px] border-r border-gray-200 px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-4">
                          Prosjektert mengde
                        </th>
                        <th className="min-w-[120px] border-r border-gray-200 px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-4">
                          Før-måling
                        </th>
                        <th className="min-w-[52px] px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-4">
                          Handlinger
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {variables.map((variable, index) => (
                        <tr key={variable.id} className="hover:bg-gray-50">
                          {/* Reference */}
                          <td className="border-r border-gray-200 px-3 py-2 whitespace-nowrap sm:px-4 sm:py-3">
                            <input
                              ref={(el) => {
                                inputRefs.current[`reference-${variable.id}`] = el;
                              }}
                              type="text"
                              className={`min-h-11 w-full sm:min-h-0 ${RV_INPUT_COMPACT}`}
                              placeholder="Referanse"
                              value={variable.reference}
                              onChange={(e) =>
                                handleVariableChange(
                                  variable.id,
                                  "reference",
                                  e.target.value
                                )
                              }
                              onBlur={() => handleReferenceBlur(variable)}
                              onKeyDown={(e) => handleReferenceKeyDown(e, variable, index)}
                              autoFocus={index === 0 && open}
                            />
                          </td>
                          {/* Projected Quantity */}
                          <td className="border-r border-gray-200 px-3 py-2 whitespace-nowrap sm:px-4 sm:py-3">
                            <input
                              ref={(el) => {
                                inputRefs.current[`projected-${variable.id}`] = el;
                              }}
                              type="number"
                              step="0.01"
                              className={`min-h-11 w-full sm:min-h-0 ${RV_INPUT_COMPACT}`}
                              placeholder="0.00"
                              value={variable.projected_quantity}
                              onChange={(e) =>
                                handleVariableChange(
                                  variable.id,
                                  "projected_quantity",
                                  e.target.value
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
                                  e.preventDefault();
                                  // Move to pre-measurement field in same row
                                  const nextInputRef = inputRefs.current[`pre-measurement-${variable.id}`];
                                  if (nextInputRef) {
                                    nextInputRef.focus();
                                  }
                                }
                              }}
                            />
                          </td>
                          {/* Pre-measurement Quantity */}
                          <td className="border-r border-gray-200 px-3 py-2 whitespace-nowrap sm:px-4 sm:py-3">
                            <input
                              ref={(el) => {
                                inputRefs.current[`pre-measurement-${variable.id}`] = el;
                              }}
                              type="number"
                              step="0.01"
                              className={`min-h-11 w-full sm:min-h-0 ${RV_INPUT_COMPACT}`}
                              placeholder="0.00"
                              value={variable.pre_measurement_quantity}
                              onChange={(e) =>
                                handleVariableChange(
                                  variable.id,
                                  "pre_measurement_quantity",
                                  e.target.value
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
                                  e.preventDefault();
                                  // Move to next row's reference field
                                  const nextIndex = index + 1;
                                  if (nextIndex < variables.length) {
                                    const nextInputRef = inputRefs.current[`reference-${variables[nextIndex].id}`];
                                    if (nextInputRef) {
                                      nextInputRef.focus();
                                    }
                                  } else {
                                    // If this is the last row and reference is filled, create new row
                                    const currentVariable = variables[index];
                                    if (currentVariable.reference && currentVariable.reference.trim() !== "") {
                                      const newVariable = {
                                        id: uuidv4(),
                                        reference: "",
                                        projected_quantity: "",
                                        pre_measurement_quantity: "",
                                      };
                                      setVariables([...variables, newVariable]);
                                      setTimeout(() => {
                                        const newInputRef = inputRefs.current[`reference-${newVariable.id}`];
                                        if (newInputRef) {
                                          newInputRef.focus();
                                        }
                                      }, 50);
                                    }
                                  }
                                }
                              }}
                            />
                          </td>
                          {/* Actions */}
                          <td className="px-3 py-2 text-right whitespace-nowrap sm:px-4 sm:py-3">
                            {variables.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveRow(variable.id)}
                                className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-red-600 hover:bg-red-50 sm:h-9 sm:w-9"
                                title="Fjern rad"
                                aria-label="Fjern rad"
                              >
                                <TrashIcon className="h-6 w-6 sm:h-5 sm:w-5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end sm:gap-3">
                  <button
                    type="button"
                    className={`${RV_BTN_OUTLINE} !min-h-11 w-full sm:w-auto`}
                    onClick={onClose}
                    disabled={isSaving}
                  >
                    Avbryt
                  </button>
                  <button
                    type="button"
                    className={`${RV_BTN_PRIMARY_DARK} !min-h-11 w-full sm:w-auto`}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? "Legger til..." : `Legg til (${variables.filter(v => v.reference && v.reference.trim() !== "").length})`}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BulkAddVariablesDialog;

